import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  RefreshCw,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Plus,
  Search,
  Play,
  X,
  Filter,
  User,
  ListTodo,
} from 'lucide-react';
import { tasksApi } from '@/api/endpoints/tasks.api';
import type { TaskStatus, TaskType, TaskPriority } from '@/types/task.types';
import { TaskStatusLabels, TaskTypeLabels, TaskPriorityLabels } from '@/types/task.types';
import { CreateTaskDialog } from '@/components/features/tasks';

const priorityColors: Record<TaskPriority, { bg: string; text: string }> = {
  LOW: { bg: 'bg-gray-100', text: 'text-gray-800' },
  MEDIUM: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  HIGH: { bg: 'bg-orange-100', text: 'text-orange-800' },
  URGENT: { bg: 'bg-red-100', text: 'text-red-800' },
};

const statusColors: Record<TaskStatus, { bg: string; text: string }> = {
  PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  IN_PROGRESS: { bg: 'bg-blue-100', text: 'text-blue-800' },
  COMPLETED: { bg: 'bg-green-100', text: 'text-green-800' },
  CANCELLED: { bg: 'bg-gray-100', text: 'text-gray-800' },
};

type TabType = 'all' | 'my' | 'overdue' | 'unassigned';

export default function TasksPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Tab and filters from URL
  const currentTab = (searchParams.get('tab') || 'all') as TabType;
  const statusFilter = searchParams.get('status') || '';
  const typeFilter = searchParams.get('type') || '';
  const priorityFilter = searchParams.get('priority') || '';
  const searchQuery = searchParams.get('search') || '';
  const overdueOnly = searchParams.get('overdue') === 'true';
  const unassignedOnly = searchParams.get('unassigned') === 'true';
  const currentPage = parseInt(searchParams.get('page') || '0', 10);

  // Query for stats (cached)
  const { data: stats } = useQuery({
    queryKey: ['tasks', 'stats'],
    queryFn: () => tasksApi.getStats(),
    staleTime: 1000 * 60 * 2,
  });

  // Query for my tasks (always needed for tab count)
  const { data: myTasks = [] } = useQuery({
    queryKey: ['tasks', 'my-pending'],
    queryFn: () => tasksApi.getMyPending(),
    staleTime: 1000 * 60 * 2,
  });

  // Main tasks query based on current tab and filters
  const {
    data: tasksData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['tasks', currentTab, { page: currentPage, status: statusFilter, type: typeFilter, priority: priorityFilter, search: searchQuery, overdue: overdueOnly, unassigned: unassignedOnly }],
    queryFn: async () => {
      if (currentTab === 'my') {
        const data = await tasksApi.getMyPending();
        return { content: data, totalPages: 1, totalElements: data.length };
      } else if (currentTab === 'overdue') {
        return tasksApi.getOverdue({ page: currentPage, size: 20 });
      } else if (currentTab === 'unassigned') {
        return tasksApi.getUnassigned({ page: currentPage, size: 20 });
      } else {
        return tasksApi.getAll({
          page: currentPage,
          size: 20,
          search: searchQuery || undefined,
          statuses: statusFilter ? [statusFilter as TaskStatus] : undefined,
          types: typeFilter ? [typeFilter as TaskType] : undefined,
          priorities: priorityFilter ? [priorityFilter as TaskPriority] : undefined,
          overdueOnly: overdueOnly || undefined,
          unassignedOnly: unassignedOnly || undefined,
        });
      }
    },
    staleTime: 1000 * 60 * 2,
    placeholderData: keepPreviousData,
  });

  const tasks = tasksData?.content || [];
  const totalPages = tasksData?.totalPages || 0;
  const totalElements = tasksData?.totalElements || 0;

  const updateFilter = (key: string, value: string | boolean | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === null || value === '' || value === false) {
      newParams.delete(key);
    } else {
      newParams.set(key, String(value));
    }
    newParams.set('page', '0');
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  const setTab = (tab: TabType) => {
    const newParams = new URLSearchParams();
    if (tab !== 'all') {
      newParams.set('tab', tab);
    }
    setSearchParams(newParams);
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const search = formData.get('search') as string;
    updateFilter('search', search);
  };

  const handleStartTask = async (taskId: string) => {
    try {
      await tasksApi.start(taskId);
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    } catch (err) {
      console.error('Erro ao iniciar tarefa:', err);
    }
  };

  const handleCompleteTask = async (taskId: string) => {
    try {
      await tasksApi.complete(taskId, {});
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    } catch (err) {
      console.error('Erro ao concluir tarefa:', err);
    }
  };

  const hasActiveFilters = statusFilter || typeFilter || priorityFilter || searchQuery || overdueOnly || unassignedOnly;

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-red-800">Erro ao carregar tarefas. Tente novamente.</p>
        <button onClick={() => refetch()} className="mt-2 rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700">
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Tarefas</h1>
          <p className="text-muted-foreground">
            {stats?.totalPending || 0} pendente{stats?.totalPending !== 1 ? 's' : ''}
            {(stats?.totalOverdue || 0) > 0 && ` • ${stats?.totalOverdue} atrasada${stats?.totalOverdue !== 1 ? 's' : ''}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} className="flex items-center gap-2 rounded-lg border px-4 py-2 hover:bg-muted">
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
          <button onClick={() => setShowCreateDialog(true)} className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90">
            <Plus className="h-4 w-4" />
            Nova Tarefa
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg border bg-muted/30 p-1">
        <button onClick={() => setTab('all')} className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${currentTab === 'all' ? 'bg-background shadow-sm' : 'hover:bg-background/50'}`}>
          <ListTodo className="h-4 w-4" />
          Todas
        </button>
        <button onClick={() => setTab('my')} className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${currentTab === 'my' ? 'bg-background shadow-sm' : 'hover:bg-background/50'}`}>
          <User className="h-4 w-4" />
          Minhas ({myTasks.length})
        </button>
        <button onClick={() => setTab('overdue')} className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${currentTab === 'overdue' ? 'bg-background shadow-sm' : 'hover:bg-background/50'} ${(stats?.totalOverdue || 0) > 0 ? 'text-red-600' : ''}`}>
          <AlertTriangle className="h-4 w-4" />
          Atrasadas ({stats?.totalOverdue || 0})
        </button>
        <button onClick={() => setTab('unassigned')} className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${currentTab === 'unassigned' ? 'bg-background shadow-sm' : 'hover:bg-background/50'}`}>
          <Clock className="h-4 w-4" />
          Não atribuídas
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <div className={`cursor-pointer rounded-lg border bg-card p-4 transition-colors hover:bg-muted/50 ${statusFilter === 'PENDING' ? 'ring-2 ring-primary' : ''}`} onClick={() => updateFilter('status', statusFilter === 'PENDING' ? '' : 'PENDING')}>
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-yellow-500" />
            <span className="text-sm text-muted-foreground">Pendentes</span>
          </div>
          <p className="mt-2 text-2xl font-bold">{stats?.totalPending || 0}</p>
        </div>
        <div className={`cursor-pointer rounded-lg border bg-card p-4 transition-colors hover:bg-muted/50 ${overdueOnly ? 'ring-2 ring-primary' : ''}`} onClick={() => updateFilter('overdue', overdueOnly ? null : true)}>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <span className="text-sm text-muted-foreground">Atrasadas</span>
          </div>
          <p className="mt-2 text-2xl font-bold">{stats?.totalOverdue || 0}</p>
        </div>
        <div className={`cursor-pointer rounded-lg border bg-card p-4 transition-colors hover:bg-muted/50 ${statusFilter === 'IN_PROGRESS' ? 'ring-2 ring-primary' : ''}`} onClick={() => updateFilter('status', statusFilter === 'IN_PROGRESS' ? '' : 'IN_PROGRESS')}>
          <div className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-blue-500" />
            <span className="text-sm text-muted-foreground">Em Andamento</span>
          </div>
          <p className="mt-2 text-2xl font-bold">{stats?.totalInProgress || 0}</p>
        </div>
        <div className={`cursor-pointer rounded-lg border bg-card p-4 transition-colors hover:bg-muted/50 ${statusFilter === 'COMPLETED' ? 'ring-2 ring-primary' : ''}`} onClick={() => updateFilter('status', statusFilter === 'COMPLETED' ? '' : 'COMPLETED')}>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-500" />
            <span className="text-sm text-muted-foreground">Concluídas</span>
          </div>
          <p className="mt-2 text-2xl font-bold">{stats?.totalCompleted || 0}</p>
        </div>
      </div>

      {/* Filters - only show on 'all' tab */}
      {currentTab === 'all' && (
        <div className="flex flex-wrap items-center gap-4">
          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input type="text" name="search" placeholder="Buscar tarefas..." defaultValue={searchQuery} className="rounded-lg border bg-background py-2 pl-10 pr-4" />
            </div>
            <button type="submit" className="rounded-lg border px-3 py-2 hover:bg-muted">
              <Search className="h-4 w-4" />
            </button>
          </form>

          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <select value={statusFilter} onChange={(e) => updateFilter('status', e.target.value)} className="rounded-lg border bg-background px-3 py-2">
              <option value="">Todos os status</option>
              {Object.entries(TaskStatusLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <select value={typeFilter} onChange={(e) => updateFilter('type', e.target.value)} className="rounded-lg border bg-background px-3 py-2">
              <option value="">Todos os tipos</option>
              {Object.entries(TaskTypeLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <select value={priorityFilter} onChange={(e) => updateFilter('priority', e.target.value)} className="rounded-lg border bg-background px-3 py-2">
              <option value="">Todas as prioridades</option>
              {Object.entries(TaskPriorityLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <button onClick={() => updateFilter('unassigned', unassignedOnly ? null : true)} className={`flex items-center gap-1 rounded-lg border px-3 py-2 ${unassignedOnly ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
              <User className="h-4 w-4" />
              Não atribuídas
            </button>
          </div>

          {hasActiveFilters && (
            <button onClick={clearFilters} className="flex items-center gap-1 text-sm text-primary hover:underline">
              <X className="h-4 w-4" />
              Limpar filtros
            </button>
          )}
        </div>
      )}

      {/* Tasks List */}
      <div className="rounded-lg border bg-card">
        {isLoading && tasks.length === 0 ? (
          <div className="flex h-64 items-center justify-center">
            <RefreshCw className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2">Carregando...</span>
          </div>
        ) : tasks.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center text-muted-foreground">
            <CheckCircle2 className="mb-2 h-12 w-12" />
            <p>Nenhuma tarefa encontrada</p>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="mt-2 text-sm text-primary hover:underline">
                Limpar filtros
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="divide-y">
              {tasks.map((task) => (
                <div key={task.id} className="p-4 hover:bg-muted/50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">{task.title}</h3>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${priorityColors[task.priority].bg} ${priorityColors[task.priority].text}`}>
                          {task.priorityLabel}
                        </span>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusColors[task.status].bg} ${statusColors[task.status].text}`}>
                          {task.statusLabel}
                        </span>
                        {task.overdue && task.status !== 'COMPLETED' && task.status !== 'CANCELLED' && (
                          <span className="flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                            <AlertTriangle className="h-3 w-3" />
                            Atrasada
                          </span>
                        )}
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1 rounded bg-muted px-2 py-1">{task.typeLabel}</span>
                        {task.patientName && <span>Paciente: {task.patientName}</span>}
                        <span>Responsável: {task.assignedToName || 'Não atribuído'}</span>
                        {task.dueDate && <span>Prazo: {new Date(task.dueDate).toLocaleDateString('pt-BR')}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {task.status === 'PENDING' && (
                        <button onClick={() => handleStartTask(task.id)} className="flex items-center gap-1 rounded-lg border px-3 py-1 text-sm hover:bg-muted" title="Iniciar tarefa">
                          <Play className="h-4 w-4" />
                          Iniciar
                        </button>
                      )}
                      {task.status === 'IN_PROGRESS' && (
                        <button onClick={() => handleCompleteTask(task.id)} className="flex items-center gap-1 rounded-lg bg-green-600 px-3 py-1 text-sm text-white hover:bg-green-700" title="Concluir tarefa">
                          <CheckCircle2 className="h-4 w-4" />
                          Concluir
                        </button>
                      )}
                      <button onClick={() => navigate(`/tasks/${task.id}`)} className="rounded-lg border px-3 py-1 text-sm hover:bg-muted">
                        Ver detalhes
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t p-4">
                <span className="text-sm text-muted-foreground">Mostrando {tasks.length} de {totalElements} tarefas</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => updateFilter('page', String(currentPage - 1))} disabled={currentPage === 0} className="rounded-lg border px-3 py-1 text-sm hover:bg-muted disabled:opacity-50">
                    Anterior
                  </button>
                  <span className="text-sm">Página {currentPage + 1} de {totalPages}</span>
                  <button onClick={() => updateFilter('page', String(currentPage + 1))} disabled={currentPage >= totalPages - 1} className="rounded-lg border px-3 py-1 text-sm hover:bg-muted disabled:opacity-50">
                    Próxima
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Task Dialog */}
      {showCreateDialog && (
        <CreateTaskDialog
          onClose={() => setShowCreateDialog(false)}
          onSuccess={() => {
            setShowCreateDialog(false);
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
          }}
        />
      )}
    </div>
  );
}
